import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, RotateCcw, Umbrella, Clock } from "lucide-react";
import LavoratoreForm from "@/pages/lavoratori/LavoratoreForm";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const EMPTY_FORM = {
  nome: "",
  cognome: "",
  mansione: "Operaio",
  societa: "",
  telefono: "",
  email: "",
  contratto: "Tempo indeterminato",
  note: "",
  costo_orario: 25,
  retribuzione_oraria: 12,
  contributi_pct: 30,
  tfr_pct: 7.4,
  mensilita_aggiuntive_pct: 8.3,
  indennita: 0,
  trasferta: 0,
  trasferta_in_costi_fissi: false,
  trasferta_in_costi_variabili: false,
  assicurazione_mensile: 0,
  ore_ferie_annuali: 208,
  ore_permesso_annuali: 104,
  active: true,
};

export default function Lavoratori() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create, id = edit
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: items = [] } = useQuery({
    queryKey: ["lavoratori", showInactive],
    queryFn: () => api.get("/lavoratori", { params: showInactive ? { include_inactive: true } : {} }).then((r) => r.data),
  });

  // Saldi Ferie & Permessi aggiornati mese per mese (maturati fino al mese corrente,
  // meno utilizzati, con riporti ed eventuali aggiustamenti manuali).
  const currentYear = new Date().getFullYear();
  const [saldiAnno, setSaldiAnno] = useState(currentYear);
  const { data: saldi = [] } = useQuery({
    queryKey: ["ferie-saldi", saldiAnno],
    queryFn: () => api.get("/ferie/saldi", { params: { anno: saldiAnno } }).then((r) => r.data),
  });
  const saldiByLav = React.useMemo(() => {
    const m = {};
    for (const s of saldi) m[s.lavoratore_id] = s;
    return m;
  }, [saldi]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (lav) => {
    setEditingId(lav.lavoratore_id);
    // Migrazione soft: se il record legacy non ha ore_ferie_annuali, deriva da giorni × 8
    const oreFerie =
      lav.ore_ferie_annuali != null
        ? Number(lav.ore_ferie_annuali)
        : lav.giorni_ferie_annuali != null
        ? Number(lav.giorni_ferie_annuali) * 8
        : 208;
    setForm({
      ...EMPTY_FORM,
      ...lav,
      ore_ferie_annuali: oreFerie,
      email: lav.email || "",
      telefono: lav.telefono || "",
      contratto: lav.contratto || "Tempo indeterminato",
      note: lav.note || "",
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditingId(null);
  };

  const save = useMutation({
    mutationFn: (payload) => {
      if (editingId) {
        return api.patch(`/lavoratori/${editingId}`, payload).then((r) => r.data);
      }
      return api.post("/lavoratori", payload).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(editingId ? "Anagrafica aggiornata" : "Lavoratore creato");
      qc.invalidateQueries({ queryKey: ["lavoratori"] });
      closeModal();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore durante il salvataggio"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/lavoratori/${id}`),
    onSuccess: () => {
      toast.success("Lavoratore disattivato (riattivabile in qualsiasi momento)");
      qc.invalidateQueries({ queryKey: ["lavoratori"] });
    },
  });

  const reactivate = useMutation({
    mutationFn: (id) => api.post(`/lavoratori/${id}/reattiva`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(
        data?.user_reactivated
          ? "Lavoratore riattivato — account utente ripristinato"
          : "Lavoratore riattivato"
      );
      qc.invalidateQueries({ queryKey: ["lavoratori"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const handleDelete = (l) => {
    const ok = window.confirm(
      `Eliminare ${l.nome} ${l.cognome}?\n\n` +
        `Verrà disattivato immediatamente:\n` +
        `• account utente disabilitato (nessun login)\n` +
        `• rimosso da tutti i cantieri e dagli eventi calendario\n` +
        `• nessuna notifica push residua\n\n` +
        `Lo storico rimane per audit e potrai RIATTIVARLO in qualsiasi momento ` +
        `spuntando "Mostra disattivati" e cliccando Riattiva.`
    );
    if (ok) del.mutate(l.lavoratore_id);
  };

  const isEditing = !!editingId;

  return (
    <div className="space-y-6" data-testid="page-lavoratori">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Anagrafica</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Lavoratori</h1>
          <p className="text-slate-500 text-sm mt-1">
            Modifica i dati anagrafici: lo storico resta legato al lavoratore.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="toggle-show-inactive">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4"
            />
            Mostra disattivati
          </label>
          <button
            onClick={openCreate}
            data-testid="btn-new-lavoratore"
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm shadow-brutal"
          >
            <Plus className="w-4 h-4" /> Nuovo lavoratore
          </button>
        </div>
      </div>

      {/* Saldi Ferie & Permessi — aggiornati automaticamente mese per mese */}
      <div className="bg-white border border-slate-200" data-testid="saldi-ferie-permessi">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">Saldi mensili</div>
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Umbrella className="w-5 h-5 text-sky-600" /> Ferie & Permessi
              <span className="text-xs font-normal text-slate-500">
                — aggiornati automaticamente mese per mese
              </span>
            </h2>
          </div>
          <select
            value={saldiAnno}
            onChange={(e) => setSaldiAnno(Number(e.target.value))}
            data-testid="saldi-anno"
            className="border border-slate-300 rounded-sm px-2 py-1 text-sm bg-white"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums" data-testid="table-saldi">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th rowSpan={2} className="text-left p-2 border-r border-slate-200">Lavoratore</th>
                <th colSpan={5} className="text-center p-2 border-r border-slate-200 bg-sky-50">🏖️ Ferie (ore)</th>
                <th colSpan={5} className="text-center p-2 bg-amber-50">⏱️ Permessi (ore)</th>
              </tr>
              <tr>
                <th className="text-right p-2">Annuali</th>
                <th className="text-right p-2">Maturate</th>
                <th className="text-right p-2">Utilizzate</th>
                <th className="text-right p-2 bg-sky-50">Residue oggi</th>
                <th className="text-right p-2 border-r border-slate-200">Residue fine anno</th>
                <th className="text-right p-2">Annuali</th>
                <th className="text-right p-2">Maturati</th>
                <th className="text-right p-2">Utilizzati</th>
                <th className="text-right p-2 bg-amber-50">Residui oggi</th>
                <th className="text-right p-2">Residui fine anno</th>
              </tr>
            </thead>
            <tbody>
              {items.filter((l) => l.active !== false).map((l) => {
                const s = saldiByLav[l.lavoratore_id] || {};
                // Ferie: usa ore_ferie_* dal backend (già in ore); fallback su giorni × 8 per dati legacy.
                const fmt = (v) => (v == null || v === "" ? "—" : `${Math.round(Number(v))} h`);
                const feAnn = s.ore_ferie_annuali ?? l.ore_ferie_annuali ?? (l.giorni_ferie_annuali ? l.giorni_ferie_annuali * 8 : null);
                const feMat = s.ore_ferie_maturate ?? null;
                const feUsa = s.ore_ferie_usate ?? 0;
                const feRes = s.ore_ferie_disponibili_ora ?? s.ore_ferie_residue ?? null;
                const feEoY = s.ore_ferie_residue_fine_anno ?? null;
                return (
                  <tr key={l.lavoratore_id} className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`saldo-row-${l.lavoratore_id}`}>
                    <td className="p-2 font-semibold border-r border-slate-200">{l.nome} {l.cognome}</td>
                    <td className="text-right p-2">{fmt(feAnn)}</td>
                    <td className="text-right p-2 text-slate-500">{fmt(feMat)}</td>
                    <td className="text-right p-2 text-rose-700">{`${Math.round(Number(feUsa) || 0)} h`}</td>
                    <td className="text-right p-2 font-bold bg-sky-50">{fmt(feRes)}</td>
                    <td className="text-right p-2 border-r border-slate-200">{fmt(feEoY)}</td>
                    <td className="text-right p-2">{s.ore_permesso_annuali ?? l.ore_permesso_annuali ?? "—"}</td>
                    <td className="text-right p-2 text-slate-500">{s.ore_permesso_maturate ?? "—"}</td>
                    <td className="text-right p-2 text-rose-700">{s.ore_permesso_usate ?? 0}</td>
                    <td className="text-right p-2 font-bold bg-amber-50">{s.ore_permesso_residue ?? "—"}</td>
                    <td className="text-right p-2">{s.ore_permesso_residue_fine_anno ?? "—"}</td>
                  </tr>
                );
              })}
              {items.filter((l) => l.active !== false).length === 0 && (
                <tr><td colSpan={11} className="p-6 text-center text-slate-400 italic">Nessun lavoratore attivo</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-slate-200 text-[10px] text-slate-500 bg-slate-50">
          <b>Maturate/Maturati</b>: quota accumulata pro-rata sui mesi trascorsi.
          <b> Residue oggi</b>: quello che il lavoratore può davvero prendere adesso.
          <b> Residue fine anno</b>: proiezione al 31/12 (annuali + riporti − utilizzate).
          I saldi si aggiornano automaticamente quando approvi una ferie o un permesso dal Calendario.
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Mansione</th>
              <th className="text-left p-3">Contratto</th>
              <th className="text-left p-3">Contatti</th>
              <th className="text-right p-3">Costo azienda €/h</th>
              <th className="text-right p-3">Ferie/anno</th>
              <th className="text-right p-3">Permessi/anno</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr
                key={l.lavoratore_id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${
                  l.active === false ? "opacity-50" : ""
                }`}
                data-testid={`lavoratore-row-${l.lavoratore_id}`}
              >
                <td className="p-3 font-medium">
                  {l.nome} {l.cognome}
                  {l.active === false && (
                    <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                      inattivo
                    </span>
                  )}
                  {l.societa && <div className="text-xs text-slate-500">{l.societa}</div>}
                </td>
                <td className="p-3">{l.mansione}</td>
                <td className="p-3 text-xs">{l.contratto || "—"}</td>
                <td className="p-3 text-xs">
                  {l.telefono && <div>📞 {l.telefono}</div>}
                  {l.email && <div className="text-slate-500">{l.email}</div>}
                  {!l.telefono && !l.email && <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right font-mono font-bold">{eur(l.costo_orario)}</td>
                <td className="p-3 text-right font-mono text-xs">
                  {l.ore_ferie_annuali ?? (l.giorni_ferie_annuali != null ? l.giorni_ferie_annuali * 8 : 208)} h
                </td>
                <td className="p-3 text-right font-mono text-xs">
                  {l.ore_permesso_annuali ?? 104} h
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {l.active === false ? (
                    <button
                      onClick={() => reactivate.mutate(l.lavoratore_id)}
                      data-testid={`btn-reactivate-lavoratore-${l.lavoratore_id}`}
                      title="Riattiva"
                      disabled={reactivate.isPending}
                      className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs font-semibold">Riattiva</span>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => openEdit(l)}
                        data-testid={`btn-edit-lavoratore-${l.lavoratore_id}`}
                        title="Modifica"
                        className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 mr-3"
                      >
                        <Pencil className="w-4 h-4" />
                        <span className="hidden sm:inline text-xs font-semibold">Modifica</span>
                      </button>
                      <button
                        onClick={() => handleDelete(l)}
                        title="Elimina"
                        data-testid={`btn-delete-lavoratore-${l.lavoratore_id}`}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-400">
                  Nessun lavoratore
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {open && (
        <LavoratoreForm
          isEditing={isEditing}
          form={form}
          setForm={setForm}
          onClose={closeModal}
          onSave={(f) => save.mutate(f)}
          saving={save.isPending}
        />
      )}
    </div>
  );
}
